/**
 * RequireAuth - Route guard that redirects to login if not authenticated.
 */

import { Navigate, useLocation } from 'react-router-dom'
import { Loader2 } from 'lucide-react'
import { useAuth } from '@/hooks/useAuth'
import { useT } from '@/contexts/I18nContext'
import { stashReturnPath } from '@/lib/auth-redirect'
import { track } from '@/lib/betterstack'
import type { ReactNode } from 'react'
import { useEffect, useRef } from 'react'

export function RequireAuth({ children }: { children: ReactNode }) {
  const { isAuthenticated, isLoading } = useAuth()
  const t = useT()
  const location = useLocation()
  // Once per RequireAuth mount — the funnel only needs the first entry into
  // the guarded app. (Module flags / render-time ref writes trip purity rules.)
  const appEnterTracked = useRef(false)

  // First-run funnel: fired once when the guarded app actually renders.
  // Gap this closes: signups whose session exists but who never see the app.
  useEffect(() => {
    if (isAuthenticated && !appEnterTracked.current) {
      appEnterTracked.current = true
      track('app-enter', { path: location.pathname })
    }
  }, [isAuthenticated, location.pathname])

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-[100dvh] bg-surface">
        <div className="text-center">
          <Loader2 className="w-8 h-8 animate-spin text-primary mx-auto mb-4" />
          <p className="text-on-surface-variant font-medium">{t('auth.loading')}</p>
        </div>
      </div>
    )
  }

  if (!isAuthenticated) {
    // Preserve where they were heading (e.g. /app?checkout=success) so the
    // OAuth round-trip can return them there — see auth-redirect.ts. Skip when
    // already on /login so the bounce's own re-render doesn't clobber the real
    // target with /login (defensive: in prod /login sits outside RequireAuth).
    if (location.pathname !== '/login') {
      stashReturnPath(location.pathname + location.search)
    }
    return <Navigate to="/login" replace />
  }

  return <>{children}</>
}
